import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Play, Pause, RotateCcw, MapPin, Siren, SendHorizonal, Camera, Mic, Film, ChevronRight } from "lucide-react";
import { useT } from "../lib/lang";

/**
 * In-app animated tutorial that plays like a short video.
 *
 * Four ~3-second steps × translated caption + animated mock-phone UI.
 * The four step captions come from the active i18n locale (FR / EN)
 * so a returning user who switches the language sees the walkthrough in
 * their preferred tongue.
 */

const STEP_MS = 3200;

const FRAME_W = 220;
const FRAME_H = 360;

const Step1 = ({ progress }) => {
  // Animated press-and-hold ring filling up around the SOS button.
  const dash = 2 * Math.PI * 38;
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-6">
      <div className="relative" style={{ width: 110, height: 110 }}>
        <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
          <circle cx="50" cy="50" r="38" stroke="rgba(127,29,29,0.15)" strokeWidth="4" fill="none" />
          <circle
            cx="50"
            cy="50"
            r="38"
            stroke="#7F1D1D"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={dash}
            strokeDashoffset={dash * (1 - Math.min(1, progress * 1.4))}
            style={{ transition: "stroke-dashoffset 80ms linear" }}
          />
        </svg>
        <div
          className="absolute inset-2 rounded-full flex items-center justify-center text-white font-black tracking-tighter"
          style={{
            backgroundImage: "radial-gradient(circle at 30% 25%, #B91C1C, #7F1D1D 60%, #450A0A)",
            fontSize: 28,
            boxShadow: "0 8px 22px -6px rgba(127,29,29,0.55)",
            transform: progress > 0.15 ? "scale(0.96)" : "scale(1)",
            transition: "transform 200ms ease",
          }}
        >
          SOS
        </div>
      </div>
      {/* Animated finger press indicator */}
      <div
        className="absolute"
        style={{
          left: "50%",
          top: "50%",
          transform: `translate(-50%, ${-10 + progress * 6}px)`,
          opacity: 0.85,
        }}
      >
        <div className="w-10 h-10 rounded-full border-4 border-white bg-mali-navy/90 shadow-lg" />
      </div>
    </div>
  );
};

const Step2 = ({ progress, t }) => {
  const types = [
    { name: t.medical || "Médical", color: "#EF4444", Icon: Siren },
    { name: t.accident || "Accident", color: "#FACC15", Icon: Siren },
    { name: t.police || "Police", color: "#3B82F6", Icon: Siren },
    { name: t.fire || "Incendie", color: "#F97316", Icon: Siren },
  ];
  // Highlight one tile, then move to the next as time progresses.
  const idx = Math.min(3, Math.floor(progress * 4));
  return (
    <div className="w-full h-full grid grid-cols-2 gap-3 p-4">
      {types.map((t, i) => {
        const active = i === idx;
        return (
          <div
            key={t.name}
            className="rounded-xl flex flex-col items-center justify-center gap-1 transition-all"
            style={{
              background: active ? `${t.color}22` : "rgba(255,255,255,0.04)",
              border: `2px solid ${active ? t.color : "rgba(120,120,140,0.25)"}`,
              transform: active ? "scale(1.04)" : "scale(1)",
              boxShadow: active ? `0 6px 18px -6px ${t.color}aa` : "none",
              padding: 8,
            }}
          >
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
              style={{ background: t.color }}
            >
              <t.Icon className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <span className="text-[10px] font-bold text-mali-navy dark:text-white">{t.name}</span>
          </div>
        );
      })}
    </div>
  );
};

const Step3 = ({ progress, t }) => {
  const items = [
    { name: t.tutorialPhoto, color: "#0EA5E9", Icon: Camera },
    { name: t.tutorialAudio, color: "#10B981", Icon: Mic },
    { name: t.tutorialVideo, color: "#A855F7", Icon: Film },
  ];
  const idx = Math.min(2, Math.floor(progress * 3));
  return (
    <div className="w-full h-full flex flex-col justify-center gap-3 px-5">
      {items.map((t, i) => {
        const active = i === idx;
        return (
          <div
            key={t.name}
            className="rounded-2xl flex items-center gap-3 transition-all px-3 py-2.5"
            style={{
              background: active ? `${t.color}22` : "rgba(255,255,255,0.04)",
              border: `2px solid ${active ? t.color : "rgba(120,120,140,0.25)"}`,
              transform: active ? "translateX(2px)" : "translateX(0)",
            }}
          >
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0"
              style={{ background: t.color }}
            >
              <t.Icon className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <span className="text-xs font-bold text-mali-navy dark:text-white">{t.name}</span>
            <span className="ml-auto text-[10px] text-muted-foreground">{active ? "✓" : ""}</span>
          </div>
        );
      })}
    </div>
  );
};

const Step4 = ({ progress, t }) => {
  // Animated paper-plane flying off the navy send button + a confirmation card.
  const lift = progress > 0.55 ? -28 - (progress - 0.55) * 70 : 0;
  const fade = progress > 0.7 ? 1 - (progress - 0.7) * 3 : 1;
  return (
    <div className="w-full h-full flex flex-col items-center justify-end pb-8 gap-5">
      <div
        className="rounded-xl px-4 py-2.5 text-white font-black text-[11px] tracking-[0.18em] uppercase flex items-center gap-2 shadow-md"
        style={{
          backgroundImage: "linear-gradient(135deg, #1E2A52 0%, #0A1128 60%, #050818)",
          minWidth: 170,
          justifyContent: "center",
        }}
      >
        <SendHorizonal
          className="w-4 h-4 transition-all"
          style={{ transform: `translate(${progress * 16}px, ${lift}px)`, opacity: fade }}
        />
        <span style={{ opacity: progress > 0.7 ? 0 : 1, transition: "opacity 200ms" }}>
          {t.tutorialSendLabel}
        </span>
      </div>

      {/* Confirmation pill appears in the second half */}
      <div
        className="rounded-full px-3 py-1.5 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5 shadow-lg transition-all"
        style={{
          opacity: progress > 0.7 ? 1 : 0,
          transform: `translateY(${progress > 0.7 ? 0 : 8}px)`,
        }}
      >
        <MapPin className="w-3 h-3" /> {t.tutorialSentPill}
      </div>
    </div>
  );
};

const STEPS = [
  { captionKey: "tutorialStep1", Component: Step1 },
  { captionKey: "tutorialStep2", Component: Step2 },
  { captionKey: "tutorialStep3", Component: Step3 },
  { captionKey: "tutorialStep4", Component: Step4 },
];

export const TutorialModal = ({ open, onClose }) => {
  const [t] = useT();
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [ended, setEnded] = useState(false);
  // `videoReady` becomes true once we confirm `tutorial-fr.mp4` exists on the
  // backend — at which point the modal swaps the animated mock-phone for a
  // real HTML5 `<video>` element with FR/EN/BM subtitle tracks. Until the
  // file lands (Sora 2 build-step) we fall back to the in-app animation.
  const [videoReady, setVideoReady] = useState(false);
  const tickRef = useRef(null);
  const startRef = useRef(0);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const videoUrl = `${apiBase}/api/uploads/tutorial-fr.mp4`;
  const vttUrl = (lang) => `${apiBase}/api/uploads/tutorial-${lang}.vtt`;

  // Probe the backend once on open. A HEAD request is cheap and avoids any
  // visible flicker — if the MP4 is absent we never even mount the <video>.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetch(videoUrl, { method: "HEAD" })
      .then((r) => { if (!cancelled) setVideoReady(r.ok); })
      .catch(() => { if (!cancelled) setVideoReady(false); });
    return () => { cancelled = true; };
  }, [open, videoUrl]);

  // Reset on every open
  useEffect(() => {
    if (open) {
      Promise.resolve().then(() => {
        setStep(0);
        setProgress(0);
        setPlaying(true);
        setEnded(false);
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open || !playing || videoReady) {
      cancelAnimationFrame(tickRef.current);
      return undefined;
    }
    startRef.current = performance.now();
    const loop = (now) => {
      const elapsed = now - startRef.current;
      const p = Math.min(1, elapsed / STEP_MS);
      setProgress(p);
      if (p >= 1) {
        if (step + 1 < STEPS.length) {
          setStep((s) => s + 1);
          startRef.current = performance.now();
          setProgress(0);
          tickRef.current = requestAnimationFrame(loop);
        } else {
          setPlaying(false);
          setEnded(true);
        }
      } else {
        tickRef.current = requestAnimationFrame(loop);
      }
    };
    tickRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(tickRef.current);
  }, [open, playing, step, videoReady]);

  const restart = () => {
    setStep(0);
    setProgress(0);
    setEnded(false);
    setPlaying(true);
  };

  const togglePlay = () => {
    if (ended) {
      restart();
      return;
    }
    setPlaying((p) => !p);
  };

  // Compute overall progress for the segmented bar at the top.
  const totalProgress = useMemo(
    () => ((step + Math.min(1, progress)) / STEPS.length) * 100,
    [step, progress],
  );

  if (!open) return null;

  const current = STEPS[step];
  const StepComp = current.Component;

  return (
    <div
      className="fixed inset-0 z-[500] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tutorial-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
    >
      <div
        className="w-full max-w-sm rounded-3xl overflow-hidden bg-card text-card-foreground border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-rescue-ink text-white">
          <div className="min-w-0">
            <div className="text-[9px] font-bold tracking-[0.3em] uppercase text-mali-yellow">{t.tutorialKicker}</div>
            <h3 id="tutorial-title" className="font-display text-base font-black tracking-tight leading-none mt-1">
              {t.tutorialTitle}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="tutorial-close"
            className="p-2 -m-2 text-white/70 hover:text-white transition-colors"
            aria-label={t.tutorialClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Segmented progress bar — only shown for the in-app animation; the
            real <video> player has its own native progress controls. */}
        {!videoReady && (
          <div className="px-4 pt-3 grid grid-cols-4 gap-1.5">
            {STEPS.map((s, i) => {
              const fill =
                i < step ? 100 : i === step ? Math.min(100, progress * 100) : 0;
              // STEPS is a fixed-length static array of step descriptors —
              // its identity is stable across renders, so deriving a unique
              // key from each step (title) rather than its index avoids the
              // React array-index-as-key footgun without changing behavior.
              const stepKey = (s && (s.id || s.title)) || `step-${i}`;
              return (
                <div key={stepKey} className="h-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-mali-red transition-all"
                    style={{ width: `${fill}%` }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Media region — prefer the Sora 2 MP4 when available, otherwise
            fall back to the in-app animated mock-phone walkthrough. */}
        <div className="px-4 py-5 flex justify-center">
          {videoReady ? (
            <video
              key={t.tutorialTitle}  // re-mount on language switch to refresh tracks
              src={videoUrl}
              controls
              playsInline
              preload="metadata"
              autoPlay
              crossOrigin="anonymous"
              data-testid="tutorial-video"
              className="rounded-2xl shadow-xl w-full max-w-[260px] aspect-[9/16] bg-black"
            >
              {/* Multi-locale captions — the browser shows the track whose
                  srclang matches the active UI locale (default = French). */}
              <track default kind="subtitles" srcLang="fr" label="Français"
                     src={vttUrl("fr")} />
              <track kind="subtitles" srcLang="en" label="English"
                     src={vttUrl("en")} />
            </video>
          ) : (
            <div
              className="rounded-[28px] border-4 border-mali-navy/85 dark:border-white/20 bg-background overflow-hidden relative shadow-xl"
              style={{ width: FRAME_W, height: FRAME_H }}
            >
              {/* Tiny status notch */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-14 h-1.5 rounded-full bg-mali-navy/40 dark:bg-white/20" />
              <div className="absolute inset-0 flex">
                <StepComp progress={progress} t={t} />
              </div>
            </div>
          )}
        </div>

        {/* Caption — under the animation only; real video has its own subtitles. */}
        {!videoReady && (
          <div className="px-5 pb-1 text-center min-h-[3.25rem]" data-testid="tutorial-caption">
            <div className="text-[9px] font-bold tracking-[0.25em] uppercase text-mali-red">
              {t.tutorialStep} {step + 1} {t.tutorialStepOf} {STEPS.length}
            </div>
            <p className="font-display text-base font-black text-mali-navy dark:text-white tracking-tight leading-snug mt-1">
              {t[current.captionKey]}
            </p>
          </div>
        )}

        {/* Footer controls */}
        <div className="px-4 pb-5 pt-3 flex items-center justify-between gap-2">
          <span className="text-[10px] text-muted-foreground font-mono">
            {t.tutorialDuration}
          </span>
          <div className="flex items-center gap-1">
            {/* Pause / Replay only show when the in-app animation is driving the
                playback. The real <video> has native controls so we'd just
                duplicate them. */}
            {!videoReady && (
              <button
                type="button"
                onClick={togglePlay}
                data-testid="tutorial-toggle"
                className="px-3 py-2 rounded-full bg-muted text-foreground hover:bg-muted/70 text-[11px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5 transition-colors"
              >
                {ended ? (
                  <><RotateCcw className="w-3.5 h-3.5" /> {t.tutorialReplay}</>
                ) : playing ? (
                  <><Pause className="w-3.5 h-3.5" /> {t.tutorialPause}</>
                ) : (
                  <><Play className="w-3.5 h-3.5" /> {t.tutorialPlay}</>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="tutorial-got-it"
              className="px-3 py-2 rounded-full bg-mali-navy text-white text-[11px] font-bold uppercase tracking-[0.18em] hover:opacity-95 transition-opacity flex items-center gap-1.5"
            >
              {t.tutorialGotIt} <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
